import React, { useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

interface ProductLoaderProps {
  productName: string;
  onLoadComplete: () => void;
}

export const ProductLoader: React.FC<ProductLoaderProps> = ({ productName, onLoadComplete }) => {
  const [fadeOut, setFadeOut] = useState(false);
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const locale = searchParams.get('locale') || '';
  
  // Check for specific product types based on URL
  const isMaldaMango = location.pathname.includes('malda-mango');
  const isTea = location.pathname.includes('tea');

  useEffect(() => {
    // Start fade-out animation after 3.5 seconds
    const fadeTimeout = setTimeout(() => {
      setFadeOut(true);
    }, 3500);

    // Complete loading after 4 seconds
    const completeTimeout = setTimeout(() => {
      onLoadComplete();
    }, 4000);

    return () => {
      clearTimeout(fadeTimeout);
      clearTimeout(completeTimeout);
    };
  }, [onLoadComplete]);

  // Define all logos
  const dailoKrishiLogo = { src: "/dailokrishi-logo.svg", alt: "DailoKrishi Logo", fallback: "/logo/dailokrishi.png" };
  
  // Conditionally set logos based on URL
  let logosToDisplay;
  
  if (isMaldaMango) {
    logosToDisplay = [
      dailoKrishiLogo,
      { src: "https://upload.wikimedia.org/wikipedia/commons/1/13/Rts_logo.png", alt: "RTS Logo", fallback: "https://upload.wikimedia.org/wikipedia/commons/1/13/Rts_logo.png" },
      { src: "https://upload.wikimedia.org/wikipedia/commons/thumb/2/23/Emblem_of_Nepal.svg/250px-Emblem_of_Nepal.svg.png", alt: "Government Logo", fallback: "https://upload.wikimedia.org/wikipedia/commons/thumb/2/23/Emblem_of_Nepal.svg/250px-Emblem_of_Nepal.svg.png" },
    ];
  } else if (isTea) {
    logosToDisplay = [
      dailoKrishiLogo,
      { src: "https://market.foodsocial.io/cdn/shop/collections/Nepal_Tea_Collective.png?v=1720020734", alt: "Tea Logo", fallback: "https://market.foodsocial.io/cdn/shop/collections/Nepal_Tea_Collective.png?v=1720020734" },
    ];
  } else {
    logosToDisplay = [dailoKrishiLogo];
  }

  // Determine the text to display based on locale
  const getLoaderText = () => {
    if (locale === 'en') {
      return `The story of ${productName}`;
    } else {
      return `${productName} को कथा`;
    }
  };

  return (
    <div 
      className={`fixed inset-0 bg-white z-50 flex flex-col items-center justify-center transition-opacity duration-500 ${
        fadeOut ? 'opacity-0' : 'opacity-100'
      }`}
    >
      <div className="flex justify-center items-center gap-6 mb-8">
        {logosToDisplay.map((logo, index) => (
          <div key={index} className="w-20 h-20 flex items-center justify-center">
            <img 
              src={logo.src} 
              alt={logo.alt} 
              className="max-w-full max-h-full object-contain"
              onError={(e) => {
                // @ts-ignore - currentTarget.src is valid
                e.currentTarget.src = logo.fallback;
              }}
            />
          </div>
        ))}
      </div>
      <h2 className={`text-2xl md:text-3xl font-bold text-[rgba(0,63,40,1)] ${locale === 'en' ? 'font-slate' : 'font-noto'} text-center px-4`}>
        {getLoaderText()}
      </h2>
      <div className="mt-8 relative w-32 h-1 bg-gray-200 rounded-full overflow-hidden">
        <div className="absolute top-0 left-0 h-full bg-[#FFC700] animate-loaderProgress"></div>
      </div>
    </div>
  );
};
